import { textForStatusCode } from "@app/layouts/ErrorLayout/ErrorLayoutTypes";
import {
  AffiliateShoppingData,
  ImageCrop,
  SectionData,
  StaticContent,
  StoryData,
} from "@app/types/ServerCustomContext";
import { isSponsoredStory } from "@app/utils/sponsored";
import { configSingleStory, configSite } from "@pub/config";
import placeholders from "@pub/placeholders";
import { TRouteWithRedirect } from "@sphtech/web2-core/ssr";

import { BaseData, InputDataMap, MetaDataProps, MetaType } from "./types";

const filterCanonicalUrl = (url: string = "") => {
  // if canonical URL belongs to same site, then no need to override.
  return url.startsWith(configSite.address) ? undefined : url;
};

export const isStoryData = (
  data: InputDataMap[MetaType] | null,
): data is StoryData => {
  return !!data && "processedElements" in data;
};

export const isAffiliateShoppingData = (
  data?: InputDataMap[MetaType] | null,
): data is AffiliateShoppingData => {
  return (
    !!data &&
    typeof data === "object" &&
    "template" in data &&
    data.template === "affiliateShopping"
  );
};

export const isSectionData = (
  data: InputDataMap[MetaType] | null,
): data is SectionData => {
  return (
    !!data &&
    "listedStories" in data &&
    "topStories" in data &&
    "uniqSectionName" in data
  );
};

export const mapStoryDataToMetaData = (data: StoryData): MetaDataProps => {
  const homeSection = data.sections.length && data.sections[0];

  return {
    type: "Article",
    title: data.seoFields.metaTitle || data.processedElements.headline,
    description:
      data.seoFields.metaDescription ||
      data.processedElements.standfirst ||
      data.processedElements.bodyElements.find((e) => e.type === "paragraph")
        ?.value,
    image:
      data.processedElements.coverImage?.crops.landscape ||
      placeholders.landscape,
    path: data.path,
    canonicalUrl: filterCanonicalUrl(data.seoFields.canonicalUrl),
    robots:
      data.seoFields.metaRobots === "1"
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    ogFields: data.ogFields,
    storyData: data,
    breadcrumb: homeSection
      ? [homeSection.parent, homeSection]
          .filter((section): section is StoryData["sections"][0] => !!section)
          .map((section) => ({
            name: section.name,
            item: section.path,
          }))
      : undefined,
    sponsored: isSponsoredStory(data.sponsorship, data.typeName),
  };
};

export const mapSectionDataToMetaData = (data: SectionData): MetaDataProps => {
  // take the first story exludin the anchor block
  const firstStory =
    (data.topStories.length > 1 && data.topStories[1]) ||
    (!!data.listedStories.length && data.listedStories[0]);

  const title = data.seoFields?.metaTitle
    ? data.seoFields.metaTitle
    : data.tagSlug
      ? data.name
      : (data.listGroup?.activeTitle !== "All" &&
          data.listGroup?.activeTitle) ||
        data.name;

  return {
    type: "CollectionPage",
    path: data.path,
    title: title,
    description: data.seoFields?.metaDescription || "",
    canonicalUrl: filterCanonicalUrl(data.seoFields?.canonicalUrl),
    robots:
      data.seoFields?.metaRobots === "noindex, nofollow"
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    ogFields: {
      title: data.seoFields?.ogTitle || title,
      description: data.seoFields?.ogDescription || "",
    },
    breadcrumb:
      data.listGroup?.activeTitle !== "All" && data.listGroup?.activeTitle
        ? [
            {
              name: data.name,
              item: data.path.split("/").slice(0, -1).join("/"),
            },
          ]
        : undefined,
    image: firstStory ? firstStory.image?.crops.landscape : undefined,
  };
};

export function getAuthors(
  storyData: StoryData,
): { text: string; href?: string; title?: string; image?: ImageCrop }[] {
  if (storyData.displaySetting?.hideAuthorFlag) {
    return [];
  }

  if (storyData.byline) {
    return [{ text: storyData.byline }];
  }

  if (storyData.authors?.length) {
    return storyData.authors.map((author) => ({
      text: author.name,
      href: author.profile?.urlPath,
      title: author.profile?.title,
      image: author.profile?.image,
    }));
  }

  if (configSingleStory.byline?.defaultCredit) {
    return [
      {
        text: configSingleStory.byline.defaultCredit,
        href: "/about-us",
      },
    ];
  }
  return [];
}

export const mapStaticContentDataToMetaData = (
  data: AffiliateShoppingData | StaticContent,
): MetaDataProps => {
  const title =
    data.seoFields.metaTitle ||
    (isAffiliateShoppingData(data)
      ? data.headline
      : data.processedElements.headline);
  const image = isAffiliateShoppingData(data)
    ? data.coverImage?.crops.landscape
    : data.processedElements.coverImage?.crops.landscape;

  return {
    type: "CollectionPage",
    path: data.path,
    title: title || "",
    description: data.seoFields.metaDescription || "",
    canonicalUrl: filterCanonicalUrl(data.seoFields.canonicalUrl),
    robots:
      data.seoFields.metaRobots === "1"
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    ogFields: data.ogFields,
    image: image,
  };
};

const metaTitle = (data: InputDataMap[MetaType]) => {
  const seoFields = "seoFields" in data ? data.seoFields : undefined;
  const processedElements =
    "processedElements" in data ? data.processedElements : undefined;

  if (isStoryData(data)) {
    return seoFields?.metaTitle || processedElements?.headline || "";
  }

  if (isAffiliateShoppingData(data)) {
    return seoFields?.metaTitle || data.headline || "";
  }

  if (isSectionData(data)) {
    return (
      seoFields?.metaTitle ||
      (data.tagSlug && data.name) ||
      (data.listGroup?.activeTitle !== "All" && data.listGroup?.activeTitle) ||
      data.name
    );
  }

  return seoFields?.metaTitle || processedElements?.headline || "";
};

const metaDescription = (data: InputDataMap[MetaType]) => {
  const seoFields = "seoFields" in data ? data.seoFields : undefined;

  if (seoFields?.metaDescription) {
    return seoFields.metaDescription;
  }

  const processedElements =
    "processedElements" in data ? data.processedElements : undefined;

  return (
    processedElements?.standfirst ||
    processedElements?.bodyElements.find((e) => e.type === "paragraph")
      ?.value ||
    ""
  );
};

const metaOgFields = (data: InputDataMap[MetaType]) => {
  if (isSectionData(data)) {
    const seoFields = data.seoFields;

    return {
      title: seoFields?.ogTitle || metaTitle(data),
      description: seoFields?.ogDescription || "",
    };
  }

  return "ogFields" in data ? data.ogFields : undefined;
};

const metaImage = (data: InputDataMap[MetaType]): ImageCrop | undefined => {
  if (isAffiliateShoppingData(data) && data.coverImage?.crops.landscape) {
    return data.coverImage.crops.landscape;
  }

  if (isSectionData(data)) {
    // take the first story excluding the anchor block
    const firstStory =
      (data.topStories.length > 1 && data.topStories[1]) ||
      (!!data.listedStories.length && data.listedStories[0]);

    return firstStory ? firstStory.image?.crops.landscape : undefined;
  }

  const processedElements =
    "processedElements" in data ? data.processedElements : undefined;

  return (
    processedElements?.coverImage?.crops.landscape || placeholders.landscape
  );
};

const metaRobots = (metaRobotsValue?: string): MetaDataProps["robots"] => {
  return metaRobotsValue === "1" || metaRobotsValue === "noindex, nofollow"
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
};

const metaSectionBreadcrumb = (data: SectionData) => {
  const activeTitle = data.listGroup?.activeTitle;
  return activeTitle && activeTitle !== "All"
    ? [
        {
          name: data.name,
          item: (() => {
            const segments = data.path.split("/").filter(Boolean);
            if (segments.length <= 1) return "/";
            return "/" + segments.slice(0, -1).join("/");
          })(),
        },
      ]
    : undefined;
};

const metaStoryBreadcrumb = (data: StoryData) => {
  const homeSection = data.sections.length && data.sections[0];

  return homeSection
    ? [homeSection.parent, homeSection]
        .filter((section): section is StoryData["sections"][0] => !!section)
        .map((section) => ({
          name: section.name,
          item: section.path,
        }))
    : undefined;
};

const metaBaseData = (data: InputDataMap[MetaType]): BaseData => {
  const seoFields = "seoFields" in data ? data.seoFields : undefined;

  return {
    path: "path" in data ? data.path : "",
    title: metaTitle(data),
    description: metaDescription(data),
    canonicalUrl: filterCanonicalUrl(seoFields?.canonicalUrl),
    robots: metaRobots(seoFields?.metaRobots),
    ogFields: metaOgFields(data),
    image: metaImage(data),
  };
};

export const mapDataToMetaData = (
  data: InputDataMap[MetaType],
  pageType: MetaType,
): MetaDataProps => {
  const baseMetaDataObj = metaBaseData(data);

  // Handle Article with StoryData
  if (pageType === "Article" && isStoryData(data)) {
    return {
      type: "Article",
      ...baseMetaDataObj,
      storyData: data,
      breadcrumb: metaStoryBreadcrumb(data),
      sponsored: isSponsoredStory(data.sponsorship, data.typeName),
    };
  }

  // Handle CollectionPage
  if (pageType === "CollectionPage") {
    if (isSectionData(data)) {
      return {
        type: pageType,
        ...baseMetaDataObj,
        breadcrumb: metaSectionBreadcrumb(data),
      };
    }

    return { type: pageType, ...baseMetaDataObj };
  }

  return {
    type: "WebPage",
    ...baseMetaDataObj,
  };
};

export const mapErrorDataToMetaData = (
  loaderData: TRouteWithRedirect<unknown, string>,
  location: { pathname: string },
): MetaDataProps => {
  const statusCode = loaderData.statusCode;
  const text =
    statusCode === 400 || statusCode === 404 || statusCode === 503
      ? textForStatusCode[statusCode]
      : textForStatusCode[500];

  return {
    type: "WebPage",
    title: text.pageTitle,
    path: location.pathname,
    robots: statusCode === 404 ? "noindex" : undefined,
  };
};
